/**
 * useFontSize — the signed-in user's display text size (row 4bd890ab).
 *
 * Mounted by AppShell so the stored size is applied app-wide on every page,
 * and by the two controls that change it (the account menu's "Presentation
 * size" switch and Profile -> Appearance -> Display -> Font Size). All
 * mounts stay in step through FONT_SIZE_EVENT (this tab) and the storage
 * event (other tabs). See lib/displayScale.ts for the mechanism.
 */
import { useCallback, useEffect, useLayoutEffect, useState } from "react";

import { useAuthStore } from "@/stores/auth";
import {
  FONT_SIZE_EVENT,
  applyFontSize,
  isFontSize,
  readFontSize,
  setFontSize,
  type FontSize,
} from "@/lib/displayScale";

export function useFontSize(): [FontSize, (next: FontSize) => void] {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  // Keyed by user: a different user signing in on the same browser reads
  // their own stored size instead of inheriting the previous one.
  const [state, setState] = useState<{ userId: string | null; size: FontSize }>(() => ({
    userId,
    size: readFontSize(userId) ?? "normal",
  }));
  const size: FontSize = state.userId === userId ? state.size : (readFontSize(userId) ?? "normal");

  // Before paint, so a page never flashes at the wrong size.
  useLayoutEffect(() => {
    applyFontSize(size);
  }, [size]);

  useEffect(() => {
    const onChange = (e: Event) => {
      const next = (e as CustomEvent).detail;
      if (isFontSize(next)) setState({ userId, size: next });
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key && e.key.endsWith(`.${userId || "anonymous"}`) && e.key.startsWith("ftl.display.font_size")) {
        setState({ userId, size: isFontSize(e.newValue) ? e.newValue : "normal" });
      }
    };
    window.addEventListener(FONT_SIZE_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(FONT_SIZE_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, [userId]);

  const update = useCallback((next: FontSize) => setFontSize(userId, next), [userId]);
  return [size, update];
}
